import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
} from "@portal/ui";
import { Check, ChevronDown, Plus } from "lucide-react";
import { useState } from "react";

export interface ComboboxOption {
  value: string;
  label: string;
  /** 2 行目に小さく出す文字(メールなど)。 */
  description?: string;
  /** 絞り込みに使うほかの言葉(よみがな・メールなど)。label は必ず使う。 */
  keywords?: readonly string[];
}

/** 全角・半角と大文字・小文字の違いを無視して比べる。 */
const normalize = (s: string) => s.normalize("NFKC").toLowerCase();

/**
 * 検索できるプルダウン(ユーザー要望 2026-10-15)。押すと一覧が開き、上の欄に文字を入れると名前の部分一致で
 * 絞り込む(↑↓ と Enter でも選べる)。顧客・プロジェクト・フォルダなど、選択肢が増えていくものに使う。
 * 選択肢が少なく増えないものは、今までどおり NativeSelect にする(検索は要らない)。
 * shadcn/ui の Combobox(Popover + Command)。create を渡すと、一覧の末尾に新規作成の項目を常に出す。
 */
export function Combobox({
  id,
  "aria-label": ariaLabel,
  value,
  onChange,
  options,
  placeholder = "選択…",
  searchPlaceholder = "検索",
  emptyText = "見つかりません",
  create,
  className,
  disabled,
  onSearchChange,
}: {
  id?: string;
  "aria-label"?: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly ComboboxOption[];
  /** 何も選んでいないときにボタンに出す文字。 */
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  /** 一覧の末尾に置く新規作成(「＋ 新しい顧客を登録…」など)。絞り込んでも消えない。null なら出さない。 */
  create?: { label: string; onSelect: () => void } | null;
  className?: string;
  disabled?: boolean;
  /** 検索欄の文字が変わったとき(入力に応じて create を変えるため)。 */
  onSearchChange?: (search: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const changeSearch = (v: string) => {
    setSearch(v);
    onSearchChange?.(v);
  };
  const selected = options.find((o) => o.value === value);
  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) changeSearch("");
      }}
      modal
    >
      <PopoverTrigger asChild>
        <Button
          {...(id ? { id } : {})}
          {...(ariaLabel ? { "aria-label": ariaLabel } : {})}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          // 見た目は NativeSelect にそろえる(枠・高さ・右の ▼)
          className={cn("h-9 justify-between gap-2 px-3 font-normal shadow-xs", className)}
        >
          <span className={cn("truncate", !selected && "text-muted-foreground")}>
            {selected?.label ?? placeholder}
          </span>
          <ChevronDown aria-hidden="true" className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-(--radix-popover-trigger-width) min-w-56 p-0">
        <Command
          // 名前の部分一致で絞り込む(既定のあいまい一致より、日本語で分かりやすい)
          filter={(_value, search, keywords) =>
            normalize((keywords ?? []).join(" ")).includes(normalize(search)) ? 1 : 0
          }
        >
          <CommandInput
            placeholder={searchPlaceholder}
            value={search}
            onValueChange={changeSearch}
          />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem
                  // 同じ名前があっても見分けられるよう、値を混ぜる(絞り込みは keywords だけで行う)
                  key={o.value}
                  value={`${o.label}\u0000${o.value}`}
                  keywords={[
                    o.label,
                    ...(o.keywords ?? []),
                    ...(o.description ? [o.description] : []),
                  ]}
                  onSelect={() => {
                    onChange(o.value);
                    setOpen(false);
                    changeSearch("");
                  }}
                >
                  <Check
                    aria-hidden="true"
                    className={cn("size-4", o.value === value ? "opacity-100" : "opacity-0")}
                  />
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate">{o.label}</span>
                    {o.description && (
                      <span className="truncate text-xs text-muted-foreground">
                        {o.description}
                      </span>
                    )}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
            {create && (
              <>
                <CommandSeparator />
                <CommandGroup forceMount>
                  <CommandItem
                    forceMount
                    value="\u0000create"
                    onSelect={() => {
                      setOpen(false);
                      changeSearch("");
                      create.onSelect();
                    }}
                  >
                    <Plus aria-hidden="true" className="size-4" />
                    {create.label}
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
